import { fetchByIds } from "@/lib/large-org";
// Live Operations command center.
//
// Architecture notes (do not regress):
//   - Realtime subscriptions go through the centralized realtime-manager
//     (src/lib/realtime-manager.ts). One channel per topic, refcounted, auto
//     cleanup on unmount, dedup'd across consumers. NEVER reintroduce raw
//     `supabase.channel(...).subscribe()` here — it leaks channels and
//     duplicates work across mounts in StrictMode.
//   - Each table change triggers a *targeted* refetch, not a full reload.
//   - A single low-frequency safety poll (60s) catches anything missed
//     while the socket reconnects. The previous 30s polling loop has been
//     removed; realtime carries the load.
//   - Officer markers and battery/heartbeat status come straight from
//     `officer_live_locations` and refresh on its postgres_changes stream.
//   - Mobile: panel rail collapses behind a Sheet trigger so the map and
//     KPIs dominate the viewport on phones/tablets.
import { createFileRoute } from "@tanstack/react-router";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useAuth } from "@/lib/auth";
import { supabase } from "@/integrations/supabase/client";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { ScrollArea } from "@/components/ui/scroll-area";
import { PulseMap } from "@/components/dashboards/PulseMap";
import { AlertCenter } from "@/components/alerts/AlertCenter";
import { RealtimeStatusChip } from "@/components/RealtimeStatusChip";
import { PageBack } from "@/components/PageBack";
import { useRealtimeAlerts, alertSeverityClass } from "@/lib/patrol/use-realtime-alerts";
import { useRealtimeSubscription } from "@/lib/realtime-manager";
import { IntelligenceFeed } from "@/components/patrol-route/IntelligenceFeed";
import { OperationsRoleGate, LIVE_OPS_ROLES } from "@/components/OperationsRoleGate";
import { FieldPresenceStrip } from "@/components/dashboards/FieldPresenceStrip";
import { OnDutyRoster } from "@/components/dashboards/OnDutyRoster";
import { useFieldPresence } from "@/lib/dashboard/use-field-presence";
import {
  Activity, MapPin, ShieldAlert, Users, Clock, QrCode, BatteryLow,
  WifiOff, AlertTriangle, ChevronDown, PanelRightOpen, Timer, Radio,
} from "lucide-react";

export const Route = createFileRoute("/_authenticated/operations/live")({
  component: LiveOpsRoute,
  head: () => ({ meta: [{ title: "Live Operations — MissionReady" }] }),
});

function LiveOpsRoute() {
  return (
    <OperationsRoleGate allowed={LIVE_OPS_ROLES}>
      <LiveOpsPage />
    </OperationsRoleGate>
  );
}

type Run = {
  id: string;
  tour_id: string;
  officer_id: string | null;
  started_at: string;
  status: string;
  scanned_checkpoints: number | null;
  total_checkpoints: number | null;
  percent_complete: number | null;
};
type Scan = {
  id: number;
  checkpoint_id: string;
  scanned_at: string;
  officer_id: string;
  method: string;
};
type OfficerLoc = {
  officer_id: string;
  latitude: number | null;
  longitude: number | null;
  battery_pct: number | null;
  recorded_at: string;
  status: string | null;
  site_id?: string | null;
  name?: string | null;
};
type Geofence = {
  id: string; name: string; kind: string;
  center_lat: number; center_lng: number; radius_m: number;
  active: boolean;
};

// SLA: a patrol that has been "in_progress" longer than this with <100% is overdue.
const OVERDUE_MS = 2 * 60 * 60 * 1000;
// Officer is considered offline when last ping older than this.
const STALE_MS = 5 * 60 * 1000;
// Officer is considered idle (no movement, but heartbeat OK) — soft warning.
const IDLE_MS = 10 * 60 * 1000;

function LiveOpsPage() {
  const { currentOrg, currentRole } = useAuth();
  const orgId = currentOrg?.id;
  const canManage = ["org_owner", "admin", "supervisor"].includes(currentRole ?? "");
  // Site scoping for supervisors / regional and site supervisors. RLS already
  // limits what they can read; this keeps the view honest about their scope.
  const presence = useFieldPresence(30_000);

  const [runs, setRuns] = useState<Run[]>([]);
  const [scans, setScans] = useState<Scan[]>([]);
  const [officers, setOfficers] = useState<OfficerLoc[]>([]);
  const [sites, setSites] = useState<{ id: string; name: string; latitude: number | null; longitude: number | null }[]>([]);
  const [geofences, setGeofences] = useState<Geofence[]>([]);
  const { alerts } = useRealtimeAlerts(orgId, { toastNew: true });

  // ---- targeted loaders --------------------------------------------------
  const loadRuns = useCallback(async () => {
    if (!orgId) return;
    const { data } = await supabase.from("tour_run_summaries")
      .select("id,tour_id,officer_id,started_at,status,scanned_checkpoints,total_checkpoints,percent_complete")
      .eq("organization_id", orgId).eq("status", "in_progress")
      .order("started_at", { ascending: false }).limit(100);
    setRuns((data ?? []) as Run[]);
  }, [orgId]);

  const loadScans = useCallback(async () => {
    if (!orgId) return;
    const since = new Date(Date.now() - 4 * 3600_000).toISOString();
    const { data } = await supabase.from("checkpoint_scans")
      .select("id,checkpoint_id,scanned_at,officer_id,method")
      .gte("scanned_at", since)
      .order("scanned_at", { ascending: false }).limit(60);
    setScans((data ?? []) as Scan[]);
  }, [orgId]);

  const loadOfficers = useCallback(async () => {
    if (!orgId) return;
    // RLS decides which rows are visible: guards see only themselves, site
    // supervisors only their assigned sites, management the whole org.
    const { data } = await supabase.from("officer_live_locations")
      .select("officer_id,latitude,longitude,battery_pct,recorded_at,status,site_id")
      .eq("organization_id", orgId)
      .neq("status", "offline")
      .limit(500);
    const rows = (data ?? []) as OfficerLoc[];
    const ids = Array.from(new Set(rows.map((r) => r.officer_id)));
    let names = new Map<string, string>();
    if (ids.length > 0) {
      const { data: profiles } = await fetchByIds<any>("profiles", "id,full_name,email", ids, "id", supabase).then((d) => ({ data: d, error: null }));
      names = new Map(((profiles ?? []) as { id: string; full_name: string | null; email: string | null }[])
        .map((p) => [p.id, p.full_name || p.email || ""]));
    }
    setOfficers(rows.map((r) => ({ ...r, name: names.get(r.officer_id) || null })));
  }, [orgId]);

  const loadStaticContext = useCallback(async () => {
    if (!orgId) return;
    const [{ data: si }, { data: gf }] = await Promise.all([
      supabase.from("sites").select("id,name,latitude,longitude").eq("organization_id", orgId),
      supabase.from("geofences")
        .select("id,name,kind,center_lat,center_lng,radius_m,active")
        .eq("organization_id", orgId).eq("active", true),
    ]);
    setSites((si ?? []) as any);
    setGeofences((gf ?? []) as Geofence[]);
  }, [orgId]);

  useEffect(() => {
    if (!orgId) return;
    loadRuns(); loadScans(); loadOfficers(); loadStaticContext();
    // Single safety poll — realtime carries normal updates.
    const t = setInterval(() => { loadRuns(); loadOfficers(); }, 60_000);
    return () => clearInterval(t);
  }, [orgId, loadRuns, loadScans, loadOfficers, loadStaticContext]);

  // ---- centralized realtime (deduped + cleaned up automatically) ---------
  const topic = orgId ? `live-ops:${orgId}` : null;
  useRealtimeSubscription(topic, [
    { table: "tour_runs", filter: `organization_id=eq.${orgId}` },
  ], loadRuns);
  useRealtimeSubscription(topic ? `${topic}:scans` : null, [
    { table: "checkpoint_scans" },
  ], loadScans);
  useRealtimeSubscription(topic ? `${topic}:locs` : null, [
    { table: "officer_live_locations", filter: `organization_id=eq.${orgId}` },
  ], loadOfficers);
  useRealtimeSubscription(topic ? `${topic}:fences` : null, [
    { table: "geofences", filter: `organization_id=eq.${orgId}` },
  ], loadStaticContext);

  // ---- derived operational signals --------------------------------------
  const now = Date.now();
  const scopeIds = presence.siteIds;
  const onDutyIds = new Set(presence.onDuty.map(o => o.officerId));
  const scopedOfficers = scopeIds === null
    ? officers
    : officers.filter(o => (o.site_id && scopeIds.includes(o.site_id)) || onDutyIds.has(o.officer_id));
  const scopedSites = scopeIds === null ? sites : sites.filter(s => scopeIds.includes(s.id));
  const liveOfficers = scopedOfficers.filter(o => o.latitude != null && o.longitude != null);
  // Officers on duty whose device isn't streaming a position: place them at the
  // real GPS their punch recorded, clearly flagged as a clock-in position.
  const reportingIds = new Set(scopedOfficers.map(o => o.officer_id));
  // Off-site flag from the shared presence read, keyed by officer for the live pins.
  const offSiteByOfficer = new Map(presence.onDuty.map(o => [o.officerId, o.offSite]));
  const clockInMarkers = presence.onDuty
    .filter(o => !o.reporting && !reportingIds.has(o.officerId) && o.clockInLat != null && o.clockInLng != null)
    .map(o => ({
      officer_id: o.officerId,
      name: o.name,
      site_id: o.siteId,
      latitude: o.clockInLat as number,
      longitude: o.clockInLng as number,
      accuracy_m: o.clockInAccuracyM,
      recorded_at: o.clockInAt,
      status: "on_duty",
      battery_pct: null,
      presence: "clock_in" as const,
    }));
  const mapOfficers = [
    ...scopedOfficers.map(o => ({ ...o, offSite: offSiteByOfficer.get(o.officer_id) ?? null })),
    ...clockInMarkers,
  ];
  const siteNames = new Map(
    (scopedSites as { id: string; name?: string | null }[]).map((s) => [s.id, s.name ?? "Assigned site"]),
  );
  const onlineCount = liveOfficers.filter(o => now - new Date(o.recorded_at).getTime() < STALE_MS).length;
  const staleOfficers = liveOfficers.filter(o => {
    const age = now - new Date(o.recorded_at).getTime();
    return age >= STALE_MS;
  });
  const idleOfficers = liveOfficers.filter(o => {
    const age = now - new Date(o.recorded_at).getTime();
    return age >= IDLE_MS && age < STALE_MS;
  });
  const lowBatteryOfficers = liveOfficers.filter(o => (o.battery_pct ?? 100) < 20);

  const overdueRuns = runs.filter(r => {
    const age = now - new Date(r.started_at).getTime();
    return age >= OVERDUE_MS && (r.percent_complete ?? 0) < 100;
  });

  const openAlerts = alerts.filter(a => !a.resolved_at);
  const slaBreaches = openAlerts.filter(a =>
    a.alert_type === "sla_breach" || a.alert_type === "patrol_overdue" || a.severity === "critical");
  const missedCheckpoints = openAlerts.filter(a => a.alert_type === "missed_checkpoint");

  const breachAlerts = useMemo(
    () => openAlerts
      .filter(a => a.alert_type === "missed_checkpoint" || a.alert_type === "route_deviation")
      .map(a => ({ id: a.id, latitude: (a.metadata as any)?.lat ?? null, longitude: (a.metadata as any)?.lng ?? null, event_type: a.alert_type })),
    [openAlerts],
  );
  const sosAlerts = useMemo(
    () => openAlerts
      .filter(a => a.alert_type === "silent_panic" || a.alert_type === "sos")
      .map(a => ({ id: a.id, latitude: (a.metadata as any)?.lat ?? null, longitude: (a.metadata as any)?.lng ?? null, severity: a.severity })),
    [openAlerts],
  );

  if (!currentOrg) return <div className="p-8 text-sm text-muted-foreground">Select an organization.</div>;

  const sidePanel = (
    <div className="flex flex-col gap-3">
      <PanelGroup title="Active guard tour runs" icon={<Activity className="h-4 w-4" />} count={runs.length}>
        {runs.length === 0 ? (
          <Empty>No patrols in progress.</Empty>
        ) : (
          <ScrollArea className="max-h-[260px]">
            <div className="space-y-2 pr-1">
              {runs.map(r => {
                const overdue = now - new Date(r.started_at).getTime() >= OVERDUE_MS && (r.percent_complete ?? 0) < 100;
                const pct = r.percent_complete ?? 0;
                return (
                  <div key={r.id} className="rounded-md border p-2 text-xs">
                    <div className="flex items-center justify-between">
                      <span className="font-medium">Run {r.id.slice(0, 8)}</span>
                      <Badge variant={overdue ? "destructive" : "outline"} className="text-[10px]">
                        {pct}%{overdue && " · overdue"}
                      </Badge>
                    </div>
                    <div className="mt-1 h-1 w-full overflow-hidden rounded-full bg-muted">
                      <div className="h-full bg-primary transition-all" style={{ width: `${Math.min(100, pct)}%` }} />
                    </div>
                    <div className="mt-1 text-muted-foreground">
                      {r.scanned_checkpoints ?? 0}/{r.total_checkpoints ?? 0} scans · started {new Date(r.started_at).toLocaleTimeString()}
                    </div>
                  </div>
                );
              })}
            </div>
          </ScrollArea>
        )}
      </PanelGroup>

      <PanelGroup title="Overdue patrols" icon={<Timer className="h-4 w-4" />} count={overdueRuns.length} tone={overdueRuns.length ? "danger" : undefined}>
        {overdueRuns.length === 0 ? <Empty>None — all patrols on schedule.</Empty> : (
          <div className="space-y-1.5">
            {overdueRuns.slice(0, 8).map(r => (
              <Row
                key={r.id}
                label={`Run ${r.id.slice(0, 8)}`}
                meta={`${Math.round((now - new Date(r.started_at).getTime()) / 60000)}m elapsed · ${r.percent_complete ?? 0}%`}
                tone="danger"
              />
            ))}
          </div>
        )}
      </PanelGroup>

      <PanelGroup title="Guard status" icon={<Radio className="h-4 w-4" />} count={liveOfficers.length}>
        <div className="grid grid-cols-3 gap-2 text-center text-xs">
          <Stat label="Online" value={onlineCount} tone="ok" />
          <Stat label="Idle" value={idleOfficers.length} tone={idleOfficers.length ? "warn" : undefined} />
          <Stat label="Offline" value={staleOfficers.length} tone={staleOfficers.length ? "danger" : undefined} />
        </div>
        {(staleOfficers.length > 0 || lowBatteryOfficers.length > 0) && (
          <div className="mt-2 space-y-1">
            {staleOfficers.slice(0, 5).map(o => (
              <Row
                key={o.officer_id}
                icon={<WifiOff className="h-3 w-3" />}
                label={o.officer_id.slice(0, 8)}
                meta={`stale ${Math.round((now - new Date(o.recorded_at).getTime()) / 60000)}m`}
                tone="danger"
              />
            ))}
            {lowBatteryOfficers.slice(0, 5).map(o => (
              <Row
                key={`b-${o.officer_id}`}
                icon={<BatteryLow className="h-3 w-3" />}
                label={o.officer_id.slice(0, 8)}
                meta={`battery ${o.battery_pct ?? 0}%`}
                tone="warn"
              />
            ))}
          </div>
        )}
      </PanelGroup>

      <PanelGroup title="Missed checkpoints" icon={<AlertTriangle className="h-4 w-4" />} count={missedCheckpoints.length} tone={missedCheckpoints.length ? "warn" : undefined}>
        {missedCheckpoints.length === 0 ? <Empty>No missed checkpoints.</Empty> : (
          <div className="space-y-1.5">
            {missedCheckpoints.slice(0, 6).map(a => (
              <div key={a.id} className={`rounded border px-2 py-1.5 text-xs ${alertSeverityClass(a.severity)}`}>
                <div className="font-medium">{a.title}</div>
                <div className="text-[10px] opacity-80">{new Date(a.created_at).toLocaleTimeString()}</div>
              </div>
            ))}
          </div>
        )}
      </PanelGroup>

      <PanelGroup title="SLA breaches" icon={<ShieldAlert className="h-4 w-4" />} count={slaBreaches.length} tone={slaBreaches.length ? "danger" : undefined}>
        {slaBreaches.length === 0 ? <Empty>All SLAs nominal.</Empty> : (
          <div className="space-y-1.5">
            {slaBreaches.slice(0, 6).map(a => (
              <div key={a.id} className={`rounded border px-2 py-1.5 text-xs ${alertSeverityClass(a.severity)}`}>
                <div className="flex items-center justify-between">
                  <span className="font-medium">{a.title}</span>
                  <span className="text-[10px] uppercase">{a.severity}</span>
                </div>
                {a.body && <div className="mt-0.5 text-[10px] opacity-80 line-clamp-2">{a.body}</div>}
              </div>
            ))}
          </div>
        )}
      </PanelGroup>

      <PanelGroup title="Recent scans" icon={<Clock className="h-4 w-4" />} count={scans.length} defaultOpen={false}>
        {scans.length === 0 ? <Empty>No recent scans.</Empty> : (
          <ScrollArea className="max-h-[240px]">
            <div className="space-y-1 pr-1">
              {scans.map(s => (
                <div key={s.id} className="flex items-center justify-between rounded border p-1.5 text-xs">
                  <span className="font-mono text-[11px]">{s.checkpoint_id.slice(0, 8)}</span>
                  <span className="text-muted-foreground text-[10px]">
                    {new Date(s.scanned_at).toLocaleTimeString()} · {s.method}
                  </span>
                </div>
              ))}
            </div>
          </ScrollArea>
        )}
      </PanelGroup>
    </div>
  );

  return (
    <div className="mx-auto max-w-7xl px-3 py-4 md:px-8 md:py-6">
      <PageBack fallbackTo="/operations" label="Operations" className="mb-1 -ml-2 inline-flex min-h-[44px] items-center gap-2 rounded-lg px-2 text-sm font-semibold text-muted-foreground hover:text-foreground" />
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
        <div>
          <h1 className="text-xl font-semibold tracking-tight md:text-2xl">Live Operations</h1>
          <p className="text-xs text-muted-foreground md:text-sm">
            Real-time patrol status, officer positions, alerts, and SLA breaches.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {topic && <RealtimeStatusChip topic={topic} label="Live" />}
          <Badge variant="secondary" className="gap-1"><Activity className="h-3 w-3" /> Ops</Badge>
          {/* Mobile panel trigger */}
          <Sheet>
            <SheetTrigger asChild>
              <Button size="sm" variant="outline" className="gap-1 lg:hidden">
                <PanelRightOpen className="h-3.5 w-3.5" /> Panels
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[92vw] max-w-md overflow-y-auto p-3 sm:w-[420px]">
              <SheetTitle className="mb-3 text-base">Operations panels</SheetTitle>
              {sidePanel}
            </SheetContent>
          </Sheet>
        </div>
      </div>

      <div className="mb-4 space-y-3">
        <FieldPresenceStrip presence={presence} siteNames={siteNames} />
        <OnDutyRoster presence={presence} siteNames={siteNames} />
      </div>

      <div className="grid grid-cols-2 gap-2 md:grid-cols-4 md:gap-3">
        <Kpi icon={<Users />} label="Active patrols" value={runs.length} />
        <Kpi icon={<MapPin />} label="On-map officers" value={onlineCount} sub={staleOfficers.length ? `${staleOfficers.length} stale` : undefined} />
        <Kpi icon={<QrCode />} label="Scans (4h)" value={scans.length} />
        <Kpi icon={<ShieldAlert />} label="Open alerts" value={openAlerts.length} sub={slaBreaches.length ? `${slaBreaches.length} SLA` : undefined} tone={slaBreaches.length ? "danger" : undefined} />
      </div>

      <div className="mt-4 grid gap-4 lg:grid-cols-3">
        <Card className="overflow-hidden lg:col-span-2">
          <PulseMap
            officers={mapOfficers as any}
            sites={scopedSites as any}
            sos={sosAlerts as any}
            breaches={breachAlerts as any}
            geofences={geofences as any}
            height={520}
          />
        </Card>

        {/* Side rail — hidden on mobile, available via Sheet trigger above */}
        <div className="hidden lg:block">{sidePanel}</div>
      </div>

      <div className="mt-4 grid gap-4 lg:grid-cols-2">
        <AlertCenter organizationId={currentOrg.id} canManage={canManage} />
        <IntelligenceFeed organizationId={currentOrg.id} />
      </div>
    </div>
  );
}

// ---- presentational helpers ------------------------------------------------

function Kpi({
  icon, label, value, sub, tone,
}: { icon: React.ReactNode; label: string; value: number; sub?: string; tone?: "danger" }) {
  return (
    <Card className="flex items-center gap-3 p-3">
      <div className={`flex h-9 w-9 items-center justify-center rounded-md [&_svg]:size-4 ${
        tone === "danger" ? "bg-destructive/10 text-destructive" : "bg-primary/10 text-primary"
      }`}>
        {icon}
      </div>
      <div className="min-w-0">
        <div className="truncate text-[11px] text-muted-foreground md:text-xs">{label}</div>
        <div className="text-lg font-semibold leading-tight md:text-xl">
          {value}
          {sub && <span className="ml-1.5 text-[10px] font-normal text-muted-foreground">{sub}</span>}
        </div>
      </div>
    </Card>
  );
}

function PanelGroup({
  title, icon, count, children, defaultOpen = true, tone,
}: {
  title: string; icon: React.ReactNode; count?: number; children: React.ReactNode;
  defaultOpen?: boolean; tone?: "warn" | "danger";
}) {
  const [open, setOpen] = useState(defaultOpen);
  const toneCls =
    tone === "danger" ? "border-destructive/30" :
    tone === "warn"   ? "border-amber-500/30"   : "";
  return (
    <Card className={`p-2.5 ${toneCls}`}>
      <Collapsible open={open} onOpenChange={setOpen}>
        <CollapsibleTrigger className="flex w-full items-center justify-between gap-2 text-left text-sm font-medium">
          <span className="flex items-center gap-2">{icon}{title}</span>
          <span className="flex items-center gap-1.5">
            {count != null && (
              <Badge variant="outline" className="h-5 px-1.5 text-[10px]">{count}</Badge>
            )}
            <ChevronDown className={`h-4 w-4 transition-transform ${open ? "rotate-180" : ""}`} />
          </span>
        </CollapsibleTrigger>
        <CollapsibleContent className="mt-2">{children}</CollapsibleContent>
      </Collapsible>
    </Card>
  );
}

function Empty({ children }: { children: React.ReactNode }) {
  return <div className="py-3 text-center text-[11px] text-muted-foreground">{children}</div>;
}

function Row({ icon, label, meta, tone }: { icon?: React.ReactNode; label: string; meta?: string; tone?: "ok" | "warn" | "danger" }) {
  const cls =
    tone === "danger" ? "border-destructive/30 bg-destructive/5 text-destructive" :
    tone === "warn"   ? "border-amber-500/30 bg-amber-500/5"                       :
    tone === "ok"     ? "border-success/30 bg-success/5"                           :
                        "border-border";
  return (
    <div className={`flex items-center justify-between gap-2 rounded border px-2 py-1 text-[11px] ${cls}`}>
      <span className="flex min-w-0 items-center gap-1.5 truncate font-medium">{icon}{label}</span>
      {meta && <span className="shrink-0 text-[10px] opacity-80">{meta}</span>}
    </div>
  );
}

function Stat({ label, value, tone }: { label: string; value: number; tone?: "ok" | "warn" | "danger" }) {
  const cls =
    tone === "danger" ? "text-destructive" :
    tone === "warn"   ? "text-amber-600"   :
    tone === "ok"     ? "text-success"     :
                        "";
  return (
    <div className="rounded-md border p-2">
      <div className={`text-base font-semibold ${cls}`}>{value}</div>
      <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</div>
    </div>
  );
}
